import { useMemo } from "react";
import { PickListOptions, TicketPersonFormField } from "../../../types/reservation";
import { useFormContext } from "react-hook-form";
import { useReservationStore } from "../../../stores/useReservationStore";

const NATIONALITY_MAP = "Nationality";
const TYPE_OF_CERT_MAP = "TypeOfCertificate";
const CERT_NUMBER_MAP = "CertificationNumber";
const GREEK_NATIONALITY_VALUE = "1";
const OTHER_NATIONALITY_VALUE = "2";
const GREEK_CERT_OPTION_VALUES = new Set(["5"]);       // AMKA only
const OTHER_CERT_OPTION_VALUES = new Set(["4", "2"]);  // Άδεια παραμονής, Διαβατήριο

export function useNationalityCertPattern(
  ticketPersonFormFields: TicketPersonFormField[],
) {
  const { ticketPersonData: formData, setTicketPersonField } = useReservationStore();
  const { setValue } = useFormContext();

  const sortedFields = useMemo(
    () => [...ticketPersonFormFields].sort((a, b) => a.sortOrder - b.sortOrder),
    [ticketPersonFormFields],
  );

  const setField = (ticketId: number, fieldKey: string, value: string | null) => {
    setTicketPersonField(ticketId, fieldKey, value);
    setValue(`ticketPersonData.${ticketId}.${fieldKey}` as any, value, {
      shouldValidate: false,
    });
  };

  const hasNationalityCertPattern = useMemo(() => {
    const maps = new Set(sortedFields.map((f) => f.ticketPersonMap));
    return (
      maps.has(NATIONALITY_MAP) &&
      maps.has(TYPE_OF_CERT_MAP) &&
      maps.has(CERT_NUMBER_MAP)
    );
  }, [sortedFields]);

  const nationalityField = useMemo(
    () =>
      hasNationalityCertPattern
        ? (sortedFields.find((f) => f.ticketPersonMap === NATIONALITY_MAP) ?? null)
        : null,
    [hasNationalityCertPattern, sortedFields],
  );

  // Move TypeOfCertificate and CertificationNumber to appear right after Nationality
  const displayFields = useMemo(() => {
    if (!hasNationalityCertPattern) return sortedFields;
    const certMaps = new Set([TYPE_OF_CERT_MAP, CERT_NUMBER_MAP]);
    const certFields = sortedFields.filter((f) => certMaps.has(f.ticketPersonMap));
    const otherFields = sortedFields.filter((f) => !certMaps.has(f.ticketPersonMap));
    const nationalityIndex = otherFields.findIndex(
      (f) => f.ticketPersonMap === NATIONALITY_MAP,
    );
    if (nationalityIndex === -1) return sortedFields;
    return [
      ...otherFields.slice(0, nationalityIndex + 1),
      ...certFields,
      ...otherFields.slice(nationalityIndex + 1),
    ];
  }, [hasNationalityCertPattern, sortedFields]);

  const getCertOptions = (
    ticketId: number,
    field: TicketPersonFormField,
  ): PickListOptions[] => {
    const options = [...field.pickListOptions].sort(
      (a, b) => a.sortOrder - b.sortOrder,
    );
    if (
      !hasNationalityCertPattern ||
      field.ticketPersonMap !== TYPE_OF_CERT_MAP ||
      !nationalityField
    )
      return options;

    const nationalityValue =
      formData[ticketId]?.[nationalityField.dynamicAttributeId];
    if (nationalityValue === GREEK_NATIONALITY_VALUE)
      return options.filter((o) => GREEK_CERT_OPTION_VALUES.has(o.optionValue));
    if (nationalityValue === OTHER_NATIONALITY_VALUE)
      return options.filter((o) => OTHER_CERT_OPTION_VALUES.has(o.optionValue));
    return [];
  };

  const onNationalityChange = (ticketId: number) => {
    if (!hasNationalityCertPattern) return;
    const certTypeField = sortedFields.find(
      (f) => f.ticketPersonMap === TYPE_OF_CERT_MAP,
    );
    if (certTypeField) {
      setField(ticketId, certTypeField.dynamicAttributeId, null);
    }
  };

  return {
    displayFields,
    getCertOptions,
    onNationalityChange,
  };
}
